import type { FC } from "react";

import type {
  Money,
  Money as MoneyType,
  SubscriptionResponse,
} from "~/generated/graphql";
import type { CustomKeys } from "~/components/common/BenefitsModal/contract";
import type { FeeTypeId } from "~/components/engage/EqualProminenceInResultsMobile/contract";

export interface ItineraryData {
  transportTypes: string[];
  isMultiDestination: boolean;
  isRoundTrip: boolean;
}

export interface DualPriceStickyBarProps {
  onContinueWithFlight: () => void;
  itineraryData: ItineraryData;
  loading: boolean;
  pricePerPassenger: {
    amount: number;
    currency: string;
  };
  nonDiscountedPricePerPassenger: {
    amount: number;
    currency: string;
  };
  isFlightSummary?: boolean;
  trackedPrice?: {
    amount: number;
    currency: string;
  };
  Component?: FC<DualPriceContainerProps>;
}

export interface DualPriceContainerProps {
  offer: SubscriptionResponse;
  nonDiscountedPricePerPassenger: MoneyType;
  discountedPricePerPassenger: MoneyType;
  onContinueWithFlight: () => void;
  isVisible?: boolean;
}

export interface DualPriceContainerTemplateProps {
  offer: SubscriptionResponse;
  nonDiscountedPricePerPassenger: MoneyType;
  discountedPricePerPassenger: MoneyType;
  savings: MoneyType;
  onContinueWithDiscount: () => void;
  onContinueWithoutDiscount: () => void;
}

export interface OnContinueWithFlightProps {
  feeTypeId?: FeeTypeId;
}

export interface DualPriceContainerWrapperProps {
  offer: SubscriptionResponse;
  nonDiscountedPricePerPassenger: MoneyType;
  discountedPricePerPassenger: MoneyType;
  onContinueWithFlight: (props?: OnContinueWithFlightProps) => void;
  isVisible?: boolean;
  Template: FC<DualPriceContainerTemplateProps>;
  fromResults?: boolean;
}

export interface HandleOnContinueWithFlightProps {
  priceWithoutDiscountSelected: boolean;
}

export interface UseTrackPriceEventProps {
  isFlightSummary: boolean;
  isMeta: boolean;
  nonDiscountedPricePerPassenger: {
    amount: number;
    currency: string;
  };
}

export interface UseTrackingOnloadEventProps {
  discountedPricePerPassenger?: {
    amount: number;
    currency: string;
  };
  nonDiscountedPricePerPassenger?: {
    amount: number;
    currency: string;
  };
  isFlightSummary?: boolean;
  trackingParams: {
    label: string;
    category: string;
    action: string;
  };
}

export interface TrackOnContinueButtonClickProps {
  wasSelectedPrimePrice: boolean;
}

export interface TrackingContextProps {
  trackOnContinueButtonClick: ({
    wasSelectedPrimePrice,
  }: TrackOnContinueButtonClickProps) => void;
  trackingCategory: DualPriceTrackingCategoriesEnum;
}

export interface DualPriceContainerTrackingContextType {
  itineraryData: ItineraryData;
  trackingCategory: DualPriceTrackingCategoriesEnum;
}

export interface PrimePriceDisclaimerProps {
  offer: SubscriptionResponse;
  savings: Money;
  customKeys?: CustomKeys;
}

export enum DualPriceTrackingCategoriesEnum {
  EMPTY = "",
  FLIGHTS_RESULTS = "flights_results",
  FLIGHTS_SUMMARY = "flights_summary",
}
